import React, { useEffect, useState } from "react";
import {
  Container,
  Col,
  Row,
  Card,
  CardBody,
  InputGroup,
  Input,
  Button,
  InputGroupText,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import { formatDate } from "../../../Components/Common/FormatDate";
import ReturnBill from "../component/ReturnBill";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";

const SummaryCard = ({ title, value, color, icon }) => (
  <Card className="shadow-sm card-animate border-0">
    <CardBody className="d-flex align-items-center justify-content-between">
      <div className="w-100">
        <h6 className="fw-semibold text-uppercase" style={{ color }}>
          {title}
        </h6>
        <div className="d-flex justify-content-between w-100">
          <h4 className="fs-2 fw-bold" style={{ color }}>
            {value}
          </h4>
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: color + "20",
              color: color,
              width: "36px",
              height: "36px",
            }}
          >
            <i className={`${icon} fs-5`} />
          </div>
        </div>
      </div>
    </CardBody>
  </Card>
);

const ReturnBillLogs = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [pan, setPan] = useState("");
  const [touched, setTouched] = useState(false);
  const [filterText, setFilterText] = useState("");

  const isInvalid = touched && !remarks?.trim();

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "75px",
    },
    {
      name: "Ref Sales Invoice No",
      selector: (row) => row.refSalesInvoiceNumber,
      sortable: true,
    },
    {
      name: "Return Invoice No",
      selector: (row) => row.returnInvoiceNumber,
      sortable: true,
    },
    {
      name: "Customer Pan",
      selector: (row) => row.customerPan,
      sortable: true,
    },
    {
      name: "Return Date",
      selector: (row) => formatDate(row.returnDate),
      sortable: true,
    },
    {
      name: "Discount(%)",
      selector: (row) => row.discountPercentage,
      sortable: true,
      right: true,
    },
    {
      name: "Tax Amount",
      selector: (row) => row.taxAmount,
      sortable: true,
      right: true,
    },
    {
      name: "Total",
      selector: (row) => row.grandTotal,
      cell: (row) => <NumberWithCommas number={row.grandTotal} />,
      sortable: true,
      right: true,
    },
  ];

  const fetchReturnBill = async () => {
    try {
      setLoading(true);
      const response = await SalesReportService.returnBillList();
      setData(response);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching return bills:", error);
    }
  };

  useEffect(() => {
    fetchReturnBill();
  }, []);

  const filteredData = data.filter((item) => {
    const searchContext = `${item?.refSalesInvoiceNumber || ""} ${
      item?.returnInvoiceNumber || ""
    } ${item?.customerPan || ""}`.toLowerCase();
    return searchContext.includes(filterText.toLowerCase());
  });

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    toggleDeleteModal();
  };

  const handleReturn = async () => {
    if (!remarks?.trim()) {
      setTouched(true);
      return;
    }
    if (selectedItem) {
      try {
        await SalesReportService.returnBill({
          refSalesInvoiceNumber: selectedItem?.invoiceNumber,
          customerPan: pan,
          totalReturnAmount: selectedItem?.grandTotal,
          remarks: remarks,
        });
        fetchReturnBill();
        setRemarks("");
        setPan("");
        toast.success("Selected bill is returned successfully");
      } catch (error) {
        toast.error("Failed to return the bill");
      } finally {
        toggleDeleteModal();
        setSelectedItem(null);
      }
    }
  };

  // Calculate totals
  const totalReturns = filteredData.length;
  const totalAmount = filteredData.reduce(
    (sum, item) => sum + (item.grandTotal || 0),
    0
  );
  const totalTax = filteredData.reduce(
    (sum, item) => sum + (item.taxAmount || 0),
    0
  );

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Return Bill Report" pageTitle="Reports" />

        <div
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
            overflow: "hidden",
          }}
        >
          {/* Main Content */}
          <div className="p-3 p-lg-4 mt-1">
            <Row className="mb-3 g-2">
              <Col md={6}>
                <div className="mb-3 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1 fw-bold text-dark">Return Bill Logs</h5>
                  </div>
                </div>
              </Col>

              {/* Search Filter */}
              <Col md={6} xs={12}>
                <InputGroup>
                  <Input
                    placeholder="Search by return invoice no, customer pan & ref sales invoice no"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  <InputGroupText>
                    <i className="bx bx-search"></i>
                  </InputGroupText>
                </InputGroup>
              </Col>
            </Row>

            <hr style={{ borderColor: "#dee2e6", marginTop: "0rem" }} />

            {/* Desktop Table */}
            <div className="d-none d-lg-block">
              <CustomDataTable
                responsive
                striped
                pagination
                fixedHeader
                highlightOnHover
                pointerOnHover
                persistTableHead
                progressPending={loading}
                customStyles={{
                  headCells: {
                    style: {
                      fontSize: "0.92rem",
                      fontWeight: 610,
                    },
                  },
                  rows: {
                    style: {
                      fontSize: "0.9rem",
                    },
                  },
                }}
                progressComponent={
                  <div className="my-3 text-center">
                    <Triangle visible height="80" width="80" color="#5B71B9" />
                    <h5 className="mt-1">Loading...</h5>
                  </div>
                }
                columns={columns}
                data={filteredData}
                noDataComponent={
                  <div className="text-center my-5 w-100">
                    <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                    <h5 className="fw-semibold text-secondary">
                      No Data Found
                    </h5>
                    <p className="text-muted mb-0">
                      No return bills found matching your search.
                    </p>
                  </div>
                }
              />
            </div>

            {/* Mobile Cards */}
            <div className="d-block d-lg-none">
              {loading ? (
                <div className="my-3 text-center">
                  <Triangle visible height="80" width="80" color="#5B71B9" />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              ) : filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <Card
                    key={item.returnInvoiceNumber}
                    className="mb-2 shadow-sm"
                  >
                    <CardBody>
                      <div className="mb-2">
                        <strong>Invoice:</strong> {item.returnInvoiceNumber}{" "}
                        <span className="text-muted">
                          (Ref: {item.refSalesInvoiceNumber})
                        </span>
                      </div>
                      <div className="mb-2">
                        <strong>Customer Pan:</strong> {item.customerPan}
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <div>
                          <strong>Date:</strong> {formatDate(item.returnDate)}
                        </div>
                        <div>
                          <strong>Discount:</strong> {item.discountPercentage}%
                        </div>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <div>
                          <strong>Tax:</strong> Rs. {item.taxAmount}
                        </div>
                        <div className="fw-bold">
                          <strong>Total:</strong> Rs. {item.grandTotal}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))
              ) : (
                <div className="text-center py-5">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                  <h5 className="fw-semibold text-secondary mt-2">
                    No Data Found
                  </h5>
                  <p className="text-muted mb-0">No return bills found.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Return Modal */}
        <ReturnBill
          show={deleteModal}
          onDeleteClick={handleReturn}
          onCloseClick={toggleDeleteModal}
          selectedItem={selectedItem}
          setRemarks={setRemarks}
          setPan={setPan}
          pan={pan}
          remarks={remarks}
          isInvalid={isInvalid}
          setTouched={setTouched}
        />
      </div>
    </Container>
  );
};

export default ReturnBillLogs;
